import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  Alert,
  Box,
} from "@mui/material";
import {
  getAgents,
  linkAgentStay,
  updateAgentStay,
  deleteAgentStay,
} from "./api/agentsApi";
import AgentFormDialog from "./AgentFormDialog";
import CommissionFields, { rateIsRequired, rateIsValid } from "./CommissionFields";

// One row of `api/agents` as the picker needs it. `label` is shown and typed
// back, `search` is what's matched against.
export const agentOptionOf = (a) => ({
  id: a.id,
  agent: a,
  label: a.name || `Agent #${a.id}`,
  search: [a.name, a.email, a.phone].filter(Boolean).join(" ").toLowerCase(),
});

// Match every whitespace-separated term against name, email and phone in any
// order, mirroring the search on the Agents page.
export const filterAgents = (options, { inputValue }) => {
  const terms = String(inputValue || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return options;
  return options.filter((o) => terms.every((t) => o.search.includes(t)));
};

// The stay row carries its own link since V152 — `agentId` with `agentName` from
// the backing adult (null for a legacy adult-less agent, matching AgentDTO), and
// the commission snapshot, which the backend only returns to ADMIN.
export const linkOf = (stay) =>
  stay?.agentId == null
    ? null
    : {
        agentId: stay.agentId,
        agentName: stay.agentName || `Agent #${stay.agentId}`,
        commission: stay.commission || null,
        commissionRate: stay.commissionRate ?? null,
      };

// Link ONE stay to an agent, from the student's Stays tab. The Agents module owns
// the same action from the other direction (ConnectionDialog: pick a student, then
// a stay); here the stay is already known, so it's just the agent + the snapshot.
//
// A stay holds at most one agent — V151 narrowed the key to UNIQUE(stay_id) — so
// this doubles as the edit: an already-linked stay opens on its incumbent and can
// have the snapshot changed, the agent moved, or the link removed.
export default function LinkAgentDialog({ open, onClose, stay, studentName, onSaved }) {
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState(null);
  const [commission, setCommission] = useState("GROSS");
  const [commissionRate, setCommissionRate] = useState("20");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [newAgentOpen, setNewAgentOpen] = useState(false);

  const existing = useMemo(() => linkOf(stay), [stay]);

  const loadAgents = useCallback(
    () =>
      getAgents()
        .then((data) => {
          const list = Array.isArray(data) ? data : [];
          setAgents(list);
          return list;
        })
        .catch(() => {
          setAgents([]);
          return [];
        }),
    []
  );

  // Open on the incumbent when there is one, so the dialog starts from what's
  // true rather than from an empty picker.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);
    setConfirmUnlink(false);
    setAgent(null);
    setCommission(existing?.commission || "GROSS");
    setCommissionRate(
      existing?.commissionRate != null ? String(existing.commissionRate) : "20"
    );
    loadAgents().then((list) => {
      if (!existing) return;
      const found = list.find((a) => String(a.id) === String(existing.agentId));
      setAgent(found ? agentOptionOf(found) : { id: existing.agentId, agent: null, label: existing.agentName, search: "" });
    });
  }, [open, existing, loadAgents]);

  // Default the snapshot from whichever agent is picked — except the incumbent,
  // who keeps the snapshot stored on the link. The two drift by design: the
  // agent's defaults can move after a stay was linked at the old rate.
  const handleAgentChange = (picked) => {
    setAgent(picked);
    setError(null);
    if (!picked) return;
    if (existing && String(picked.id) === String(existing.agentId) && existing.commission) {
      setCommission(existing.commission);
      setCommissionRate(
        existing.commissionRate != null ? String(existing.commissionRate) : "20"
      );
      return;
    }
    const a = picked.agent;
    if (!a) return;
    setCommission(String(a.commission || "GROSS").toUpperCase());
    setCommissionRate(a.commissionRate != null ? String(a.commissionRate) : "20");
  };

  const isIncumbent = !!existing && !!agent && String(agent.id) === String(existing.agentId);
  const isMove = !!existing && !!agent && !isIncumbent;

  // Editing the incumbent is only worth a request if something actually changed.
  const snapshotDirty =
    !existing ||
    commission !== existing.commission ||
    Number(commissionRate) !== Number(existing.commissionRate);

  const rateOk = rateIsValid(commission, commissionRate);
  const canSave = !!agent && rateOk && !saving && (!isIncumbent || snapshotDirty);

  const finish = async () => {
    await onSaved?.();
    onClose?.();
  };

  const snapshotBody = () => ({
    commission,
    commissionRate: rateIsRequired(commission) ? Number(commissionRate) : 0,
  });

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      if (!existing) {
        await linkAgentStay(agent.id, stay.stayId, snapshotBody());
      } else if (isIncumbent) {
        await updateAgentStay(agent.id, stay.stayId, snapshotBody());
      } else {
        // UNIQUE(stay_id) means the incumbent has to go before the new link can
        // exist, and there's no endpoint that moves a stay between agents in one
        // call. If the second half fails the stay is left unlinked, so say so
        // rather than leaving them to find out from the column.
        await deleteAgentStay(existing.agentId, stay.stayId);
        try {
          await linkAgentStay(agent.id, stay.stayId, snapshotBody());
        } catch (e) {
          throw new Error(
            `Removed the link to ${existing.agentName}, but linking ${agent.label} failed ` +
              `(${e.message}). This stay now has no agent — please link it again.`
          );
        }
      }
      await finish();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!existing || saving) return;
    setSaving(true);
    setError(null);
    try {
      await deleteAgentStay(existing.agentId, stay.stayId);
      await finish();
    } catch (e) {
      setError(e);
      setConfirmUnlink(false);
    } finally {
      setSaving(false);
    }
  };

  // A freshly created agent goes straight into the picker, selected.
  const handleAgentCreated = async (saved) => {
    const list = await loadAgents();
    const found = list.find((a) => String(a.id) === String(saved?.id));
    if (found) handleAgentChange(agentOptionOf(found));
    else if (saved?.id) handleAgentChange(agentOptionOf(saved));
  };

  const options = useMemo(
    () => agents.map(agentOptionOf).sort((a, b) => a.label.localeCompare(b.label)),
    [agents]
  );

  const stayLabel = stay
    ? `${stay.arrivalDate} – ${stay.departureDate}${stay.campus ? ` · ${stay.campus}` : ""}`
    : "";

  const saveLabel = () => {
    if (saving) return "Saving…";
    if (!existing) return "Link stay";
    if (isMove) return "Move stay";
    return "Save changes";
  };

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {existing ? "Agent on this stay" : "Link this stay to an agent"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {error ? <Alert severity="error">{String(error.message || error)}</Alert> : null}

            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {studentName ? `${studentName} · ` : ""}
              {stayLabel}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Autocomplete
                sx={{ flex: 1 }}
                options={options}
                value={agent}
                onChange={(_, v) => handleAgentChange(v)}
                getOptionLabel={(o) => o.label || ""}
                filterOptions={filterAgents}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Agent"
                    placeholder="Name, email or phone"
                    size="small"
                  />
                )}
              />
              <Button size="small" sx={{ mt: 0.5 }} onClick={() => setNewAgentOpen(true)}>
                ＋ New agent
              </Button>
            </Stack>

            {isMove ? (
              <Alert severity="warning">
                This stay is linked to {existing.agentName}. Saving removes that link and
                gives the stay to {agent.label}, at the commission below.
              </Alert>
            ) : null}

            <CommissionFields
              idPrefix="link-agent"
              commission={commission}
              onCommissionChange={setCommission}
              rate={commissionRate}
              onRateChange={setCommissionRate}
            />

            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {isIncumbent
                ? "The snapshot taken when this stay was linked — editing it here doesn't touch the agent's defaults."
                : agent
                ? `Defaults from ${agent.label}${
                    agent.agent ? ` (${agent.agent.commission} ${agent.agent.commissionRate}%)` : ""
                  }; change for a per-stay rate. Snapshotted at creation.`
                : "Pick an agent to default the commission from them."}
            </Typography>

            {confirmUnlink ? (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={handleUnlink} disabled={saving}>
                    Yes, unlink
                  </Button>
                }
              >
                Remove {existing?.agentName} from this stay? Their commission on it goes
                with it.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          {existing ? (
            <Button color="error" onClick={() => setConfirmUnlink(true)} disabled={saving || confirmUnlink}>
              Unlink
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            {saveLabel()}
          </Button>
        </DialogActions>
      </Dialog>

      <AgentFormDialog
        open={newAgentOpen}
        mode="create"
        onClose={() => setNewAgentOpen(false)}
        onSaved={handleAgentCreated}
      />
    </>
  );
}
